/**
 * Where a project card's thumbnail is allowed to come from.
 *
 * The studio runs under production COEP. A `<img src>` pointing at another
 * origin is blocked and logs the console error #1268 fixed, so
 * `app-thumbnail-preview.tsx` refuses every cross-origin URL outright. That
 * refusal is correct and must NOT be relaxed — but it also meant the one
 * producer we had (the platform worker, which returns
 * `https://platform.cogo.xyz/artifacts/{slug}/thumbnail.png`) could never
 * deliver a single pixel to a card.
 *
 * The fix is an origin change, not a policy change: a stored cross-origin
 * artifact URL is rewritten to the studio's OWN proxy path, which streams the
 * same bytes back from the studio origin. Nothing cross-origin is ever
 * requested by the browser.
 *
 * A project with no stored thumbnail resolves to `null` — the card then keeps
 * its honest "no preview yet" placeholder rather than showing a stand-in that
 * pretends to be a render of the app.
 */

/** Same-origin path that streams a project's stored thumbnail bytes. */
export function studioThumbnailProxyPath(projectId: string): string {
  return `/api/thumbnails/${encodeURIComponent(projectId)}.png`
}

/**
 * Resolve the stored `studio_projects.thumbnail` value into something the
 * studio origin is allowed to load, or `null` when it cannot.
 *
 * - empty / missing            → `null` (honest placeholder)
 * - relative, `data:`, `blob:` → unchanged (already same-origin-safe)
 * - absolute same-origin       → unchanged
 * - absolute cross-origin      → studio-origin proxy path, when we know the
 *                                project id to proxy for; `null` otherwise
 *                                (an unattributable cross-origin URL stays
 *                                refused, exactly as before)
 */
export function resolveStoredProjectThumbnailSrc(
  thumbnail: string | null | undefined,
  projectId?: string | null,
): string | null {
  if (!thumbnail) return null
  const trimmed = thumbnail.trim()
  if (!trimmed) return null

  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed
  }

  const origin = typeof window === 'undefined' ? undefined : window.location.origin
  if (!origin) return null

  let url: URL
  try {
    url = new URL(trimmed, origin)
  } catch {
    return null
  }

  if (url.origin === origin) return url.toString()

  // Cross-origin. Never hand this to an <img> — route it through the studio's
  // own proxy so COEP sees a same-origin request.
  return projectId ? studioThumbnailProxyPath(projectId) : null
}
